// "You ran out of coins": shown once when the server gave the bankrupt refill
// (Me.refilledAt, see BANKRUPT_REFILL_COINS). Works on every screen: it
// watches the account, which every screen keeps up to date. The last refill
// shown is remembered per account in this browser, so it is not shown twice;
// an old refill (another device, long ago) is not shown at all.
import { useEffect, useRef, useState } from "react";
import { BANKRUPT_REFILL_COINS, type Me } from "@wp/shared";
import { formatCoins } from "../../lib/format";
import { Icon } from "./Icon";
import "../../styles/refill.css";

const SHOW_MS = 8000;
/** A refill older than this when first seen here is not announced. */
const STALE_MS = 10 * 60 * 1000;
const seenKey = (userId: string) => `wp.refillSeen.${userId}`;

function readSeen(userId: string): number {
  try {
    return Number(localStorage.getItem(seenKey(userId))) || 0;
  } catch {
    return 0;
  }
}

function writeSeen(userId: string, at: number): void {
  try {
    localStorage.setItem(seenKey(userId), String(at));
  } catch {
    // Storage blocked: the in-memory note below still stops repeats this visit.
  }
}

export function RefillNotice({ me }: { me: Me | null }) {
  const [visible, setVisible] = useState(false);
  const shown = useRef(0);
  const userId = me?.userId ?? null;
  const at = me?.refilledAt ?? null;

  useEffect(() => {
    if (!userId || !at || at <= shown.current || at <= readSeen(userId)) return;
    shown.current = at;
    writeSeen(userId, at);
    if (Date.now() - at > STALE_MS) return;
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), SHOW_MS);
    return () => clearTimeout(timer);
  }, [userId, at]);

  if (!visible) return null;
  return (
    <div className="refill-notice" role="status" data-testid="refill-notice">
      <Icon name="coin" size={22} />
      <span>
        You ran out of coins — here are <strong>{formatCoins(BANKRUPT_REFILL_COINS)}</strong> to keep playing!
      </span>
      <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => setVisible(false)}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
